import { useEffect, useMemo, useState } from 'react';
import type { FounderSubmission, SubmissionStatus, FounderSubmissionsListResponse } from '../types';
import { listFounderSubmissions, updateFounderSubmission, deleteFounderSubmission } from '../lib/api';
import { IconRefresh, IconSearch } from '../components/icons';
import { SkeletonRows } from '../components/Skeleton';
import { useToast } from '../lib/toast';
import { relativeDate } from '../lib/format';

interface Props {
  onCountChange?: (n: number) => void;
  onNouveauCountChange?: (n: number) => void;
}

const STATUS_OPTIONS: SubmissionStatus[] = ['nouveau', 'triagé', 'répondu', 'archivé'];

const STATUS_STYLE: Record<SubmissionStatus, string> = {
  nouveau: 'bg-red-50 text-red-700 border-red-200',
  triagé: 'bg-amber-soft text-amber border-amber/30',
  répondu: 'bg-funds-soft text-funds-deep border-funds/30',
  archivé: 'bg-cream-tint text-ink-muted border-line',
};

const TYPE_LABEL: Record<string, string> = {
  'audit-fdd-self': 'Audit FDD',
  'audit-fundraising-readiness': 'Audit fundraising',
  'contact-form': 'Contact',
  'premium-pack': 'Premium pack',
};

/**
 * Founders inbox (PR 6). Inbox des soumissions fondateurs.
 *
 * Pattern simple : table avec inline edit du status, du owner, des notes
 * (textarea expansible). Pas de drawer Phase 6, les cas sont assez simples.
 * Le drawer viendra en Phase 6.x si besoin d'une checklist d'exécution.
 *
 * Pip sidebar rouge si count nouveau > 0 (signal d'urgence : un founder
 * attend une réponse).
 */
export function FoundersInboxPage({ onCountChange, onNouveauCountChange }: Props) {
  const [submissions, setSubmissions] = useState<FounderSubmission[]>([]);
  const [byStatus, setByStatus] = useState<FounderSubmissionsListResponse['byStatus'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | SubmissionStatus>('nouveau');
  const [expanded, setExpanded] = useState<string | null>(null);
  const toast = useToast();

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const res = await listFounderSubmissions();
      setSubmissions(res.submissions);
      setByStatus(res.byStatus);
      onCountChange?.(res.count);
      onNouveauCountChange?.(res.byStatus.nouveau || 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load submissions');
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return submissions.filter((s) => {
      if (q) {
        const blob = `${s.submitter ?? ''} ${s.submissionType ?? ''} ${s.notesInternes ?? ''} ${s.owner ?? ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      if (filterStatus !== 'all' && s.status !== filterStatus) return false;
      return true;
    });
  }, [submissions, search, filterStatus]);

  async function patchField<K extends keyof FounderSubmission>(id: string, key: K, value: FounderSubmission[K]) {
    // Optimistic update
    setSubmissions((list) => list.map((s) => (s.id === id ? { ...s, [key]: value } : s)));
    try {
      await updateFounderSubmission(id, { [key]: value } as Partial<FounderSubmission>);
      // Recalcul byStatus si status modifié
      if (key === 'status') refresh();
    } catch (e) {
      toast.error('Update échoué', e instanceof Error ? e.message : String(e));
      refresh(); // revert
    }
  }

  async function onDelete(id: string) {
    if (!confirm('Supprimer cette soumission ?')) return;
    try {
      await deleteFounderSubmission(id);
      setSubmissions((list) => list.filter((s) => s.id !== id));
      toast.success('Soumission supprimée', '');
      refresh();
    } catch (e) {
      toast.error('Suppression échouée', e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="px-4 md:px-8 py-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-[26px] font-display tracking-tightish text-ink">
            Founders inbox
          </h1>
          <p className="mt-1 text-[13.5px] text-ink-muted max-w-2xl">
            Soumissions des fondateurs (audit FDD self-check, audit fundraising readiness,
            contact, premium pack). Triage, statut de réponse, owner.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="h-9 px-3 rounded-md border border-line bg-white text-[13px] text-ink-muted hover:bg-cream-tint transition disabled:opacity-50 flex items-center gap-1.5"
        >
          <IconRefresh className={'w-4 h-4 ' + (loading ? 'animate-spin' : '')} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
          <span className="font-medium">Erreur : </span>{error}
        </div>
      )}

      {/* KPIs */}
      {byStatus && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={
                'rounded-md border p-3 shadow-card text-left transition ' +
                (filterStatus === s
                  ? 'border-navy bg-white'
                  : 'border-line-soft bg-cream hover:border-line')
              }
            >
              <p className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">{s}</p>
              <p className="text-[22px] font-display tracking-tightish text-ink mt-1 num">{byStatus[s]}</p>
            </button>
          ))}
        </div>
      )}

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-md border border-line-soft bg-cream p-3 shadow-card">
        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <label className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">Recherche</label>
          <div className="relative">
            <IconSearch className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Email, type, notes..."
              className="h-9 w-full pl-8 pr-3 rounded-md border border-line bg-white text-[13px]"
            />
          </div>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => setFilterStatus('all')}
            className={
              'h-9 px-3 rounded-md text-[12px] font-medium border transition ' +
              (filterStatus === 'all'
                ? 'bg-navy text-white border-navy'
                : 'bg-white text-ink-muted border-line hover:bg-cream-tint')
            }
          >
            Tous
          </button>
        </div>
        <div className="ml-auto text-[12px] text-ink-muted self-end">
          <span className="num">{filtered.length}</span> sur <span className="num">{submissions.length}</span>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border border-line-soft bg-cream shadow-card overflow-hidden">
        {loading && submissions.length === 0 ? (
          <div className="p-3"><SkeletonRows rows={5} cols={5} /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-[13.5px] text-ink font-medium">
              {submissions.length === 0
                ? 'Aucune soumission pour le moment.'
                : 'Aucune soumission pour ces filtres.'}
            </p>
            <p className="mt-1 text-[12px] text-ink-muted max-w-md mx-auto">
              {submissions.length === 0
                ? "Les soumissions arrivent depuis les formulaires du site (audit, contact, premium pack)."
                : 'Essaye de cliquer Tous ou de changer la recherche.'}
            </p>
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="text-[11px] uppercase tracking-wider text-ink-faint font-medium bg-cream-tint border-b border-line-soft">
              <tr>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Submitter</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-right num">Score</th>
                <th className="px-3 py-2 text-left">Owner</th>
                <th className="px-3 py-2 text-left">Soumis</th>
                <th className="px-3 py-2 text-left w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-soft">
              {filtered.map((s) => {
                const isExpanded = expanded === s.id;
                return (
                  <>
                    <tr key={s.id} className="hover:bg-cream-tint">
                      <td className="px-3 py-2">
                        <select
                          value={s.status ?? 'nouveau'}
                          onChange={(e) => patchField(s.id, 'status', e.target.value as SubmissionStatus)}
                          className={'text-[11px] font-medium border rounded-full px-2 py-0.5 ' + (STATUS_STYLE[s.status ?? 'nouveau'])}
                        >
                          {STATUS_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-ink font-mono text-[12px] truncate max-w-[14rem]">{s.submitter || '-'}</td>
                      <td className="px-3 py-2 text-ink-muted">{TYPE_LABEL[s.submissionType || ''] || s.submissionType || '-'}</td>
                      <td className="px-3 py-2 text-right num text-ink">{typeof s.score === 'number' ? s.score : '-'}</td>
                      <td className="px-3 py-2">
                        <input
                          value={s.owner ?? ''}
                          onChange={(e) => setSubmissions((list) => list.map((x) => x.id === s.id ? { ...x, owner: e.target.value } : x))}
                          onBlur={(e) => patchField(s.id, 'owner', e.target.value)}
                          placeholder="non assigné"
                          className="text-[12px] bg-transparent w-full focus:outline-none focus:bg-white focus:border focus:border-line rounded px-1 py-0.5"
                        />
                      </td>
                      <td className="px-3 py-2 text-ink-faint text-[12px]">
                        {s.submittedAt ? relativeDate(s.submittedAt) : '-'}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => setExpanded(isExpanded ? null : s.id)}
                          className="text-[11px] text-founders-deep hover:underline"
                        >
                          {isExpanded ? 'Fermer' : 'Notes'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${s.id}-expanded`} className="bg-cream-tint/60">
                        <td colSpan={7} className="px-3 py-3">
                          <div className="space-y-2">
                            <label className="text-[10.5px] uppercase tracking-wider text-ink-faint font-medium">Notes internes</label>
                            <textarea
                              rows={3}
                              value={s.notesInternes ?? ''}
                              onChange={(e) => setSubmissions((list) => list.map((x) => x.id === s.id ? { ...x, notesInternes: e.target.value } : x))}
                              onBlur={(e) => patchField(s.id, 'notesInternes', e.target.value)}
                              placeholder="Notes admin libres, jamais renvoyées au founder"
                              className="w-full px-2 py-1.5 rounded-md border border-line text-[12.5px] bg-white"
                            />
                            <div className="flex items-center gap-3 text-[11px] text-ink-muted">
                              <label className="inline-flex items-center gap-1.5">
                                <input
                                  type="checkbox"
                                  checked={!!s.responseSent}
                                  onChange={(e) => patchField(s.id, 'responseSent', e.target.checked)}
                                />
                                Response sent
                              </label>
                              {s.responseSentAt && (
                                <span>(le {relativeDate(s.responseSentAt)})</span>
                              )}
                              <button onClick={() => onDelete(s.id)} className="ml-auto text-red-700 hover:underline">Supprimer</button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
